import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from '@clerk/clerk-react'
import { Navigate } from 'react-router-dom'
import type { ReactNode } from 'react'

import { isDevTeacherActive } from '../api/devToken'

// ───────────────────────────────────────────────────────────────────
// #832 — client-side admin-role guard.
//
// Backend ``get_current_admin`` is the real authz gate (every
// ``/api/admin/*`` route rejects non-admins with 403). This guard is
// defense-in-depth + UX: it stops the admin SPA surfaces from rendering
// for a signed-in NON-admin (e.g. a teacher who navigates to an admin
// URL), and redirects them to the home splash with a not-authorized
// toast instead of a confusing 403-on-submit dead-end.
//
// Composition: dev-token bypass → Clerk ``<SignedIn>`` auth gate →
// ``RequireAdminInner`` role check. The dev-token bypass mirrors the
// teacher wrappers so local/dev flows that skip Clerk still work.
//
// Role source: ``useAuth().orgRole``. In Clerk's org-centric model the
// admin who creates an org gets role ``'org:admin'``; teacher invitees
// get ``'org:member'``. ``orgRole`` is null when the session has no
// active org. We treat anything other than ``'org:admin'`` as non-admin.
//
// The ``isLoaded`` flag from ``useAuth`` guards the loading window.
// Clerk's ``useAuth`` reflects the current session's JWT claims, so
// ``isLoaded=false`` means claims aren't resolved yet — render nothing
// (not a redirect) so a legitimate admin isn't bounced mid-load.
// ───────────────────────────────────────────────────────────────────
export default function RequireAdmin({ children }: { children: ReactNode }) {
  if (isDevTeacherActive()) {
    return <>{children}</>
  }
  return (
    <>
      <SignedIn>
        <RequireAdminInner>{children}</RequireAdminInner>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

function RequireAdminInner({ children }: { children: ReactNode }) {
  const { isLoaded, orgRole } = useAuth()

  // Loading window: Clerk hasn't resolved the session claims yet.
  // Render nothing rather than redirecting — a confirmed admin would
  // be incorrectly bounced if we acted on a null orgRole.
  if (!isLoaded) {
    return null
  }

  // orgRole is null when the signed-in user has no active org (e.g. a
  // teacher account with no org membership, or a bare Clerk user). Not
  // an admin — redirect to splash with the not-authorized toast.
  // Also fires for org:member (teacher role in Clerk).
  if (orgRole !== 'org:admin') {
    return <Navigate to="/?not-authorized=1" replace />
  }

  return <>{children}</>
}
